/**
 * Prospects API wrapper (DNKADM-0205 U1 — the Users screen's CRM-only contacts).
 *
 * A **prospect** is a contact in the tenant's CRM mirror who has **no login and no
 * membership** yet (a lead who hasn't registered/converted). Prospects are the
 * counterpart to the login roster in `api/users.ts`; a prospect drops off the list on
 * the next sync after they accept an invite.
 *
 * CONTRACT (dink-api consumer guide — DNKAPI-0348):
 *  - `GET /v2/admin/prospects?query&page&size` → the paged {@link ProspectsPage} plus a
 *    {@link ProspectSync} status block. `query` substring-matches email/name SERVER-side;
 *    `size` is server-clamped; `total` counts all matching prospects (for the pager).
 *  - `POST /v2/admin/prospects/sync` (NO body) → the {@link ProspectSync} block with
 *    `state:'SYNCING'`. Server-debounced (~1h) — rapid re-clicks coalesce. Callers poll
 *    the list until `state` leaves `SYNCING`.
 *
 * Paging is **0-INDEXED on both sides here**: the wire is 0-indexed and this surface's
 * UI keeps a 0-indexed page too, so `toWirePage`/`toUiPage` (`api/paging.ts`) do NOT
 * apply — the same intentional exception as the member ledger.
 *
 * `NOT_CONFIGURED` (no CRM connected) is a **normal `200`** — empty `prospects`, null
 * timestamps. It is NEVER an error and is never thrown from this module.
 *
 * ERROR CONTRACT — a non-2xx propagates as the typed `ApiError` from `request()`, each
 * status distinguishable by the caller:
 *  - `403` → `code:'FORBIDDEN'` — not an admin; the caller hides the tab/action.
 *  - `404` → `code:'NOT_FOUND'` — a tenant config issue (shouldn't happen for your own
 *    tenant); an error toast.
 *  - `502` → `status:502` with the v2 business code (`UPSTREAM_UNAVAILABLE`) on
 *    `details.code` — the CRM/upstream was unreachable. DISTINCT and **retryable**: the
 *    same call is safe to repeat, and it must never be read as "no CRM connected"
 *    (that is the `NOT_CONFIGURED` 200 above).
 *  - `401` → the standard bounded refresh→retry in the shared client; no handling here.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049): bearer
 * token, NO CSRF, NO cookies, tenant server-derived from the JWT (never send a
 * tenant/slug/locationId).
 *
 * DEFENSIVE PARSE — mirrors `api/users.ts` / `api/creditTransactions.ts`: each scalar
 * degrades to `null`, an unrecognized `sync.state` is preserved RAW (never crashes), a
 * prospect with no contact id is dropped, and a non-array payload yields an empty list.
 */
import { request } from './client';

const PATH = '/v2/admin/prospects';
const SYNC_PATH = `${PATH}/sync`;

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 50;

/** The documented CRM sync lifecycle states (DNKAPI-0348). */
type KnownSyncState = 'NEVER_SYNCED' | 'SYNCED' | 'SYNCING' | 'NOT_CONFIGURED';

/**
 * The sync state on the wire — widened to tolerate an unknown future enum. Callers
 * branch on the known values and fall through to a neutral default for anything else.
 */
export type SyncState = KnownSyncState | (string & {});

/**
 * The tenant's CRM sync status. Both timestamps are ISO strings or `null`
 * (`NOT_CONFIGURED` / `NEVER_SYNCED` carry nulls); `state` is preserved RAW so an
 * unknown future value renders rather than crashing (`''` when absent).
 */
export interface ProspectSync {
  lastSyncedAt: string | null;
  nextSyncAt: string | null;
  state: SyncState;
}

/**
 * One CRM contact who is not yet a member. `contactId` is the CRM contact id — the row
 * key AND the link the invite needs (`inviteUser({ contactId })`, `api/users.ts`); a row
 * without one is dropped. Every other field may be null on the wire.
 */
export interface Prospect {
  /** The CRM contact id — dink-admin's `contactId` model contract. Never null here. */
  contactId: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  /** Whether this prospect can be invited. The server is the authority on the invite. */
  invitable: boolean;
  /** ISO last-updated timestamp from the CRM, or null. */
  dateUpdated: string | null;
}

/** Parsed `GET /v2/admin/prospects` page: the sync block + a 0-indexed page of rows. */
export interface ProspectsPage {
  tenantSlug: string | null;
  sync: ProspectSync;
  /** 0-indexed page (falls back to the requested page). */
  page: number;
  /** Server-clamped page size (falls back to the requested size). */
  size: number;
  /** All matching prospects, for the pager (falls back to the row count). */
  total: number;
  prospects: Prospect[];
}

// --- Defensive parsing helpers (mirror api/users.ts) ---------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A finite number (accepts a numeric string), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Parse the sync block. A missing/odd-typed field degrades instead of throwing; an
 * unrecognized `state` is preserved RAW (`''` when absent) so the caller's branch falls
 * through to a neutral header rather than mislabelling the tenant's CRM.
 */
function parseProspectSync(raw: unknown): ProspectSync {
  const o = isObject(raw) ? raw : {};
  return {
    lastSyncedAt: asText(o['lastSyncedAt']),
    nextSyncAt: asText(o['nextSyncAt']),
    state: asText(o['state']) ?? '',
  };
}

/**
 * Parse one prospect row DEFENSIVELY. Returns `null` for a row with no contact id (the
 * caller drops it — it is neither keyable nor invitable). `invitable` defaults to `true`
 * when absent (the contract always sends `true`); only an explicit `false` disables the
 * action. This is a display hint, NOT a security control — the server authorizes the
 * invite.
 */
function parseProspect(raw: unknown): Prospect | null {
  const o = isObject(raw) ? raw : {};
  const contactId = asText(o['crmContactId']); // DNKAPI-0348 prospects wire key mapped to our contactId model field.
  if (contactId === null) return null;
  return {
    contactId,
    email: asText(o['email']),
    name: asText(o['name']),
    phone: asText(o['phone']),
    invitable: o['invitable'] !== false,
    dateUpdated: asText(o['dateUpdated']),
  };
}

/**
 * Extract the rows array defensively — the documented envelope publishes them under
 * `prospects`; a bare array is tolerated (mirrors `listUsers`). Anything else yields an
 * empty list; rows with no contact id are dropped.
 */
function parseProspects(body: unknown): Prospect[] {
  const raw = Array.isArray(body) ? body : isObject(body) ? body['prospects'] : undefined;
  if (!Array.isArray(raw)) return [];
  return raw.map(parseProspect).filter((p): p is Prospect => p !== null);
}

function parseProspectsPage(
  body: unknown,
  fallbackPage: number,
  fallbackSize: number,
): ProspectsPage {
  const o = isObject(body) ? body : {};
  const prospects = parseProspects(body);
  return {
    tenantSlug: asText(o['tenantSlug']),
    sync: parseProspectSync(o['sync']),
    page: asNumber(o['page']) ?? fallbackPage,
    size: asNumber(o['size']) ?? fallbackSize,
    total: asNumber(o['total']) ?? prospects.length,
    prospects,
  };
}

// --- Endpoints -----------------------------------------------------------------

export interface ListProspectsParams {
  /** Substring match on email/name, matched SERVER-side; sent ONLY when non-empty. */
  query?: string;
  /** 0-indexed page; defaults 0. */
  page?: number;
  /** Page size (server-clamped); defaults 50. */
  size?: number;
}

/**
 * `GET /v2/admin/prospects?page&size[&query]` → a parsed {@link ProspectsPage}.
 * `page`/`size` are always sent; `query` ONLY when non-empty (an empty search box must
 * not narrow the list to the empty string). A `NOT_CONFIGURED` tenant is a normal `200`
 * with no prospects — not an error. Throws the typed `ApiError` on a non-2xx (see the
 * module error contract).
 */
export async function listProspects(params: ListProspectsParams = {}): Promise<ProspectsPage> {
  const page = params.page ?? DEFAULT_PAGE;
  const size = params.size ?? DEFAULT_PAGE_SIZE;
  const query = asText(params.query) ?? undefined;
  const body = await request<unknown>('GET', PATH, { query: { page, size, query } });
  return parseProspectsPage(body, page, size);
}

/**
 * `POST /v2/admin/prospects/sync` (NO body) → the parsed {@link ProspectSync}, whose
 * `state` becomes `SYNCING`. Kicks an immediate background sync; server-debounced, so
 * repeat clicks coalesce rather than queueing work. The caller then polls
 * {@link listProspects} until `state` leaves `SYNCING`. A `{ sync: … }` wrapper (the
 * list envelope's shape) is tolerated alongside the documented bare block. Throws the
 * typed `ApiError` on a non-2xx.
 */
export async function refreshProspects(): Promise<ProspectSync> {
  const body = await request<unknown>('POST', SYNC_PATH);
  const block = isObject(body) && isObject(body['sync']) ? body['sync'] : body;
  return parseProspectSync(block);
}
